import { setComValue } from './config';

export const YI_MING_CHANCE_IDENTIFICATIONS = {
  storeFormat: 'storeFormat',
  businessMode: 'ymYWMS',
  grossMargin: 'ymNBJMLL',
  trainingFee: 'ymPXF',
  recommendedStoreFormat: 'ymTJMDXZ',
  primaryTradeZone: 'primeTradeZone',
  dailyPerformance: 'ymNYRJYJ',
  annualProfit: 'ymNJLR',
  storeType: 'storeTypeStandardEdition',
  tertiaryTradeZone: 'tertiaryTradeZone',
  tradeZoneType: 'ymSQLX',
} as const;

/** 编辑态一鸣字段重新显示时允许恢复模板默认值的字段。 */
export const YI_MING_EDIT_DEFAULT_IDENTIFICATIONS = [
  'ymNSJYYTS1',
  'ymNSJYYTS2',
  'ymNSJYYTS3',
  'ymNSJYYTS4',
  'ymNSJYYTS5',
  'ymNSJYYTS6',
  'ymSBZE1',
  'ymSBZE2',
  'ymJMF1',
  'ymJMF2',
];

type YiMingChanceNames = {
  storeFormat?: string;
  businessMode?: string;
  primaryTradeZone?: string;
  storeType?: string;
  tertiaryTradeZone?: string;
  dailyPerformance?: number | string;
  annualProfit?: number | string;
};

export type YiMingChanceCalculatedValues = {
  ymNBJMLL: number | null;
  ymPXF: number | null;
  ymTJMDXZ: string | null;
  ymSQLX: string;
};

const PACKAGED_STORE_FORMATS = ['预包装', '预包装+'];
const FRESH_BAKED_STORE_FORMATS = ['现烤', '现烤+'];
const NON_DIRECT_MODES = ['委托加盟', '自主加盟', '联营店'];

const toNumber = (value: number | string | undefined) => {
  if (value === undefined || value === null || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

export const calculateYiMingChanceValues = ({
  storeFormat,
  businessMode,
  primaryTradeZone,
  storeType,
  tertiaryTradeZone,
  dailyPerformance,
  annualProfit,
}: YiMingChanceNames): YiMingChanceCalculatedValues => {
  let grossMargin: number | null = null;
  if (PACKAGED_STORE_FORMATS.includes(storeFormat || '')) {
    grossMargin = businessMode === '直营店' ? 26 : NON_DIRECT_MODES.includes(businessMode || '') ? 27.55 : null;
  } else if (FRESH_BAKED_STORE_FORMATS.includes(storeFormat || '')) {
    grossMargin = businessMode === '直营店' ? 28 : NON_DIRECT_MODES.includes(businessMode || '') ? 29.47 : null;
  }

  let trainingFee: number | null = null;
  if (businessMode === '直营店') {
    trainingFee = 0;
  } else if (NON_DIRECT_MODES.includes(businessMode || '')) {
    if (FRESH_BAKED_STORE_FORMATS.includes(storeFormat || '')) trainingFee = businessMode === '自主加盟' ? 2.8 : 0.8;
    if (PACKAGED_STORE_FORMATS.includes(storeFormat || '')) trainingFee = businessMode === '自主加盟' ? 1.2 : 0.6;
  }

  let recommendedStoreFormat: string | null = null;
  if (primaryTradeZone === '渠道') {
    recommendedStoreFormat = storeFormat || null;
  } else if (primaryTradeZone === '街铺') {
    const performance = toNumber(dailyPerformance);
    const profit = toNumber(annualProfit);
    if (performance !== null) {
      if (performance < 5000) {
        recommendedStoreFormat = '预包装';
      } else if (performance < 6500 && profit !== null) {
        recommendedStoreFormat = profit > 0 ? '预包装+' : '预包装';
      } else if (performance < 8000 && profit !== null) {
        recommendedStoreFormat = '现烤';
      } else if (performance >= 8000 && profit !== null) {
        recommendedStoreFormat = profit > 0 ? '现烤+' : '现烤';
      }
    }
  }

  return {
    ymNBJMLL: grossMargin,
    ymPXF: trainingFee,
    ymTJMDXZ: recommendedStoreFormat,
    ymSQLX: [primaryTradeZone, storeType, tertiaryTradeZone].filter(Boolean).join('·'),
  };
};

const findComponent = (components: any[], identification: string) => components.find((item) => item.identification === identification);

const getOptionName = (component: any, value: any) => {
  if (value === undefined || value === null || value === '') return undefined;
  const options = Array.isArray(component?.propertyConfigOptionVOList) ? component.propertyConfigOptionVOList : [];
  const option = options.find((item: any) => String(item.id) === String(value) || item.name === value);
  return option?.name;
};

const findOptionIdByName = (component: any, name: string | null) => {
  if (!name) return null;
  const options = Array.isArray(component?.propertyConfigOptionVOList) ? component.propertyConfigOptionVOList : [];
  return options.find((item: any) => item.name === name)?.id ?? null;
};

export const applyYiMingChanceFieldRules = ({ form, tiledFormData }: { form: any; tiledFormData: any[] }) => {
  if (!form || !Array.isArray(tiledFormData)) return;

  const getValue = (identification: string) => form.getFieldValue(identification);
  const storeFormatCom = findComponent(tiledFormData, YI_MING_CHANCE_IDENTIFICATIONS.storeFormat);
  const businessModeCom = findComponent(tiledFormData, YI_MING_CHANCE_IDENTIFICATIONS.businessMode);
  const primaryTradeZoneCom = findComponent(tiledFormData, YI_MING_CHANCE_IDENTIFICATIONS.primaryTradeZone);
  const storeTypeCom = findComponent(tiledFormData, YI_MING_CHANCE_IDENTIFICATIONS.storeType);
  const tertiaryTradeZoneCom = findComponent(tiledFormData, YI_MING_CHANCE_IDENTIFICATIONS.tertiaryTradeZone);
  const calculatedValues = calculateYiMingChanceValues({
    storeFormat: getOptionName(storeFormatCom, getValue(YI_MING_CHANCE_IDENTIFICATIONS.storeFormat)),
    businessMode: getOptionName(businessModeCom, getValue(YI_MING_CHANCE_IDENTIFICATIONS.businessMode)),
    primaryTradeZone: getOptionName(primaryTradeZoneCom, getValue(YI_MING_CHANCE_IDENTIFICATIONS.primaryTradeZone)),
    storeType: getOptionName(storeTypeCom, getValue(YI_MING_CHANCE_IDENTIFICATIONS.storeType)),
    tertiaryTradeZone: getOptionName(tertiaryTradeZoneCom, getValue(YI_MING_CHANCE_IDENTIFICATIONS.tertiaryTradeZone)),
    dailyPerformance: getValue(YI_MING_CHANCE_IDENTIFICATIONS.dailyPerformance),
    annualProfit: getValue(YI_MING_CHANCE_IDENTIFICATIONS.annualProfit),
  });

  const setValue = (identification: string, value: any) => {
    const components = tiledFormData.filter((item) => item.identification === identification);
    if (!components.length) return;
    components.forEach((item) => setComValue(item, value));
    form.setFieldValue(identification, value);
  };

  setValue(YI_MING_CHANCE_IDENTIFICATIONS.grossMargin, calculatedValues.ymNBJMLL);
  setValue(YI_MING_CHANCE_IDENTIFICATIONS.trainingFee, calculatedValues.ymPXF);

  const recommendedCom = findComponent(tiledFormData, YI_MING_CHANCE_IDENTIFICATIONS.recommendedStoreFormat);
  setValue(
    YI_MING_CHANCE_IDENTIFICATIONS.recommendedStoreFormat,
    findOptionIdByName(recommendedCom, calculatedValues.ymTJMDXZ),
  );
  setValue(YI_MING_CHANCE_IDENTIFICATIONS.tradeZoneType, calculatedValues.ymSQLX || null);
};

export const isYiMingCalculatedField = (identification: string) => [
  YI_MING_CHANCE_IDENTIFICATIONS.storeFormat,
  YI_MING_CHANCE_IDENTIFICATIONS.businessMode,
  YI_MING_CHANCE_IDENTIFICATIONS.primaryTradeZone,
  YI_MING_CHANCE_IDENTIFICATIONS.dailyPerformance,
  YI_MING_CHANCE_IDENTIFICATIONS.annualProfit,
  YI_MING_CHANCE_IDENTIFICATIONS.storeType,
  YI_MING_CHANCE_IDENTIFICATIONS.tertiaryTradeZone,
].includes(identification as any);

/** 单选清除通过 form.setFieldValue 赋值，不会触发表单的 onValuesChange。 */
export const handleYiMingChanceFieldClear = ({ form, tiledFormData, identification }: { form: any; tiledFormData: any[]; identification: string }) => {
  if (!form || !isYiMingCalculatedField(identification) || form.getFieldValue(identification) !== null) return;
  applyYiMingChanceFieldRules({ form, tiledFormData });
};
